import { useEffect, useRef, useState } from 'react';
import { Rocket, Sparkles, Tag } from 'lucide-react';
import { AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import './first-ad-guide.css';

type FirstAdGuideProps = {
  mode: 'setup' | 'ready';
  brandName?: string;
  onAddBrand: () => void;
  hasDraft?: boolean;
  onUsePrompt: (prompt: string, append?: boolean) => void;
  onBrowse: () => void;
};

const SETUP_STEPS = [
  { number: '01', label: 'Add your website and product details.' },
  { number: '02', label: 'Create an ad from a clear brief.' },
  { number: '03', label: 'Refine it and export when it is ready.' },
];

export function FirstAdGuide({ mode, brandName, hasDraft, onAddBrand, onUsePrompt, onBrowse }: FirstAdGuideProps) {
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const appliedRef = useRef(false);
  useEffect(() => { setPendingPrompt(null); }, [brandName]);
  if (mode === 'setup') {
    return (
      <section className="first-ad-guide first-ad-guide--setup" data-testid="no-brand-gate" aria-labelledby="first-ad-guide-title">
        <span className="first-ad-guide__eyebrow">YOUR FIRST AD</span>
        <h2 id="first-ad-guide-title" className="first-ad-guide__title">Start with your brand.</h2>
        <p className="first-ad-guide__intro">
          Add your website and product details so Admoji can make ads that fit your brand.
        </p>
        <ol className="first-ad-guide__steps" aria-label="How it works">
          {SETUP_STEPS.map((step) => (
            <li className="first-ad-guide__step" key={step.number}>
              <span className="first-ad-guide__number" aria-hidden="true">{step.number}</span>
              <span>{step.label}</span>
            </li>
          ))}
        </ol>
        <div className="first-ad-guide__actions">
          <button className="first-ad-guide__primary" data-testid="no-brand-gate-add-brand" type="button" onClick={onAddBrand}>
            Add your brand <span aria-hidden="true">↗</span>
          </button>
          <button className="first-ad-guide__secondary" type="button" onClick={onBrowse}>
            Browse inspiration
          </button>
        </div>
      </section>
    );
  }

  const brand = brandName?.trim();
  const brandClause = brand ? ` for ${brand}` : '';
  const suggestions = [
    { title: 'Launch a product', icon: Rocket,
      prompt: `Create a launch ad${brandClause} that makes the product or service the star. Use a striking product shot, app preview, or customer scene that fits the brand, with a clean background and a short, confident headline. Highlight one standout feature from the brand details, keep the copy minimal, and finish with an invitation to learn more.` },
    { title: 'Highlight a benefit', icon: Sparkles,
      prompt: `Create an ad${brandClause} that brings one practical benefit to life. Show a relatable customer moment where the product or service helps, and pair it with a clear headline grounded in the brand details. Keep the tone warm and direct, with one memorable takeaway and a simple call to action.` },
    { title: 'Promote an offer', icon: Tag,
      prompt: `Create a promotional ad${brandClause} with a bold visual of the product or service, a contrasting background in the brand’s colors, and one clear call to action. Make the offer easy to notice and the layout easy to read at a glance. Use an offer I’ve provided; if I haven’t shared one yet, ask me what I’d like to promote before adding a price, discount, or deadline.` },
  ];

  return (
    <section className="first-ad-guide first-ad-guide--ready first-ad-guide--compact" aria-label="Brief starting points">
      <span className="first-ad-guide__starter-label">Try an idea</span>
      <div className="first-ad-guide__starter-list">
        {suggestions.map(({title, prompt, icon: Icon}) => <button key={title} type="button" className="first-ad-guide__starter" onClick={event => {
          if (hasDraft) { triggerRef.current = event.currentTarget; appliedRef.current = false; setPendingPrompt(prompt); }
          else onUsePrompt(prompt);
        }}><Icon size={14} aria-hidden="true" />{title}</button>)}
      </div>
      <AlertDialog open={pendingPrompt !== null} onOpenChange={open => { if (!open) setPendingPrompt(null); }}>
        <AlertDialogContent className="first-ad-guide__draft-dialog" onCloseAutoFocus={event => {
          event.preventDefault();
          if (!appliedRef.current) triggerRef.current?.focus();
        }}>
          <AlertDialogTitle>Keep your brief, or start fresh?</AlertDialogTitle>
          <AlertDialogDescription>You already have a brief. Add this starting point below it, or choose to replace it. Nothing is sent until you generate.</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep my brief</AlertDialogCancel>
            <AlertDialogAction className="first-ad-guide__replace" onClick={() => { if (pendingPrompt) { appliedRef.current = true; onUsePrompt(pendingPrompt); } }}>Replace brief</AlertDialogAction>
            <AlertDialogAction className="first-ad-guide__append" onClick={() => { if (pendingPrompt) { appliedRef.current = true; onUsePrompt(pendingPrompt, true); } }}>Add to brief</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
